import { EXAM_EVENT_EXAMINEE_STATUS_LABEL } from '@exam-center/exam-event-management';
import {
  type GetExamEventSessionExamineesForTimeExtensionQuery,
  ExamEventExamineeStatus,
} from '@exam-center/graphql';
import { dayjs } from '@shared/utils';

import { type ExamineeColumn } from '../../components/examinee-time-extension-data-grid/types';
import { calculateGrantedManualDurationInMs } from '../../utils';

function getIsRowSelectable(
  status: ExamEventExamineeStatus,
  remainingExtendableTimeInMs: number,
  examEndedAt: Date | null,
  now: Date,
) {
  if (examEndedAt === null) return false;
  return (
    [
      ExamEventExamineeStatus.Registered,
      ExamEventExamineeStatus.InProgress,
      ExamEventExamineeStatus.PrepareForExam,
    ].includes(status) &&
    remainingExtendableTimeInMs > 0 &&
    dayjs(now).isBefore(dayjs(examEndedAt))
  );
}

interface MapExamSessionExamineesToExamineeTimeExtensionItemsParams {
  results: GetExamEventSessionExamineesForTimeExtensionQuery['ecGetExamEventSessionExamineesForTimeExtension']['result'];
  sessionId: string;
  now: Date;
}

export function mapExamSessionExamineesToExamineeTimeExtensionItems(
  params: MapExamSessionExamineesToExamineeTimeExtensionItemsParams,
): ExamineeColumn[] {
  const { results, now } = params;

  return results.map((result) => {
    const {
      examEventSessionExaminee,
      remainingExtendableTimeInMs,
      examEndedAt,
      manualExtendDurationInMs,
      autoExtendDurationInMs,
      maxExtendableDurationInMs,
    } = result;

    const { id } = examEventSessionExaminee;
    const { title, firstName, middleName, lastName } = examEventSessionExaminee.examinee;
    const { seatNumber, status } = examEventSessionExaminee.examEventExaminee;
    const examCourseCode = examEventSessionExaminee.examEventExaminee.examCourse.code;

    const endTime = examEndedAt ?? new Date(0);

    const statusText = EXAM_EVENT_EXAMINEE_STATUS_LABEL[status];

    const autoExtendDurationInSeconds = Math.ceil(autoExtendDurationInMs / 1_000);

    const cappedManualDurationInMs = calculateGrantedManualDurationInMs({
      rawManual: manualExtendDurationInMs,
      rawAuto: autoExtendDurationInMs,
      maxExtendable: maxExtendableDurationInMs,
    });

    return {
      id,
      title,
      seatNumber: seatNumber?.toString() ?? null,
      firstName,
      middleName: middleName ?? '',
      lastName,
      manualExtendedDurationInMs: cappedManualDurationInMs,
      autoExtendedDurationInMs: autoExtendDurationInSeconds * 1_000,
      remainingExtendableDurationInMs: remainingExtendableTimeInMs,
      endTime,
      status: statusText,
      examCourseCode,
      isExtendable: getIsRowSelectable(status, remainingExtendableTimeInMs, examEndedAt, now),
    };
  });
}
